import { t } from "i18next";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";

import CustomToastify from "@components/shared/CustomToastify/CustomToastify";
import {
  BOOKING_ADVANCE_DURATION,
  BOOKING_CONSTANTS,
  PAYMENT_OPTIONS,
  QUOTA_INFO_ARRAY,
} from "@constants/Bookings";
import { conditionalStyle, slotType } from "@enums/hooks.enum";
import { supportEnum } from "@enums/route.enum";
import { DateFormats } from "@enums/shared.enum";
import {
  AvailableTiming,
  BookingFetchResponseType,
  BookingSaveApi,
  BookingSlotApi,
  QuotaMessage,
  ResourceType,
  SingleSlot,
  timeMapObj,
} from "@models/services/booking";
import {
  getLocationType,
  locationType,
  paymentRef,
  pricingRef,
} from "@models/services/organisation";
import { userProfile } from "@models/services/user";
import { ContactDetailsType } from "@models/shared/shared.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";

export const useBooking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { selectedDate, selectedSlot, selectedTime } = useSelector(
    (State: RootState) => State.booking
  );

  const context: [
    getLocationType,
    locationType,
    userProfile,
    ContactDetailsType,
    paymentRef,
    pricingRef,
  ] = useOutletContext();

  const scrollFocusToPayment = () => {
    if (!context?.[4]?.current && !context?.[5]?.current) return;

    const target = context?.[4]?.current || context?.[5]?.current;
    target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const postToPaymentUrl = (url: string, payload: Record<string, string>) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;
    form.style.display = "none";

    for (const key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = payload[key];
        form.appendChild(input);
      }
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const bookSlot = async (payload: BookingSaveApi) => {
    dispatch(bookingSlice.actions.setPaymentLoader(true));
    await API_SERVICES.BOOKING.SAVE_BOOKING(payload).then(response => {
      const responseData = handleResponse(response);

      if (responseData?.ok) {
        if (payload?.paymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT) {
          postToPaymentUrl(
            responseData?.data?.paymentUrl,
            responseData?.data?.paymentParams
          );
        } else {
          dispatch(bookingSlice.actions.setPaymentLoader(false));
          commonUtils?.toastServiceWithOptions(
            <CustomToastify
              content={t("bookingConfirmed")}
              type="success"
              onButtonClick={() => navigate("/")}
              buttonText={t("moreBooking")}
            />,
            {
              autoClose: 20000,
              className: "fg-custom-booking-confirmed-toast-container",
            }
          );
          navigate(`${supportEnum.Reservations}?date=${payload?.date}`);
        }
        dispatch(bookingSlice.actions.flush(null));
      } else dispatch(bookingSlice.actions.setPaymentLoader(false));
    });
  };

  const buildTimeSlot = (slots: BookingFetchResponseType) => {
    if (isEmpty(slots) || !slots?.resources?.length) {
      dispatch(bookingSlice.actions.updateTimeMaps([]));
      return;
    }

    let startTime = moment.min(
      slots.resources
        .map(resource => resource.slotInfo?.slotStart)
        .filter(slotStart => slotStart)
        .map(slotStart => moment(slotStart))
    );
    const endTime = moment.max(
      slots.resources
        .map(resource => resource.slotInfo?.slotEnd)
        .filter(slotEnd => slotEnd)
        .map(slotEnd => moment(slotEnd))
    );

    const slotTime = slots?.slotSplit ?? BOOKING_CONSTANTS.WIDTH_PER_SLOT;

    const allowedTimeSlots = Array.from(
      new Set(
        slots.resources.flatMap(r => r.policyInfo?.bookingStartTimes || [])
      )
    );
    const availableSlots = Array.from(
      new Set(
        slots.resources.flatMap(r => r.slotInfo?.slots?.availableSlots || [])
      )
    );

    const occupiedSlotsArrays = slots.resources
      .filter(r => r.slotInfo?.slots?.availableSlots?.length)
      .map(r => r.slotInfo?.slots?.occupiedSlots || []);

    let occupiedSlots: string[] = [];

    if (occupiedSlotsArrays.length) {
      occupiedSlots = occupiedSlotsArrays.reduce((acc, curr) =>
        acc.filter(time => curr.includes(time))
      );
    }

    if (
      selectedDate === moment().format(DateFormats.YMD) &&
      startTime.isValid()
    ) {
      startTime = getNextTimeMark(startTime, slotTime);
    }

    if (!startTime.isValid() || availableSlots.length === 0) {
      dispatch(bookingSlice.actions.updateTimeMaps([]));
      return;
    }

    const applicableTimeSlots = generateTimeIntervals(
      startTime,
      endTime,
      slotTime
    );

    const allowedSlots: string[] = Array.from(
      new Set(
        slots.resources.flatMap(r =>
          Object.keys(r.slotInfo?.slots?.allowedDurations || {}).map(key =>
            key.replace("T_", "")
          )
        )
      )
    );

    const notAllowedSlots = applicableTimeSlots.filter(
      time => !allowedSlots.includes(time)
    );

    const updatedOccupiedSlots = [...occupiedSlots, ...notAllowedSlots];

    const slotData = {
      availableSlots,
      occupiedSlots: updatedOccupiedSlots,
      unavailableSlots: applicableTimeSlots.filter(
        time =>
          !availableSlots.includes(time) || updatedOccupiedSlots.includes(time)
      ),
    };

    const timeSlotOptions = setTimeSlotSelection(
      applicableTimeSlots,
      slotData,
      allowedTimeSlots,
      slots.resources
    );

    dispatch(
      bookingSlice.actions.changeIsSingleTimeSlot(allowedTimeSlots.length === 1)
    );
    dispatch(bookingSlice.actions.updateTimeMaps(timeSlotOptions));
  };

  const setTimeSlotSelection = (
    applicableTimeSlots: string[],
    slotData: {
      availableSlots: string[];
      occupiedSlots: string[];
      unavailableSlots: string[];
    },
    allowedTimeSlots: string[],
    resources: ResourceType[]
  ): Array<timeMapObj[]> => {
    const groupedByHour: Record<string, timeMapObj[]> = {};
    applicableTimeSlots.forEach(time => {
      const [hourStr, minuteStr] = time.split(":");
      if (
        (allowedTimeSlots.includes(minuteStr) ||
          (minuteStr === "00" && allowedTimeSlots.includes("60"))) &&
        slotData.availableSlots.includes(time)
      ) {
        let allowCount = 0;

        for (const resource of resources) {
          const allowed = resource.slotInfo?.slots?.allowedDurations;
          const key = `T_${time}`;

          const durations =
            allowed?.[key] && typeof allowed[key] === "object"
              ? Object.keys(allowed[key]).map(k =>
                  parseInt(k.replace("T_", ""), 10)
                )
              : [];

          if (durations.length === 0) continue;

          const minDuration = Math.min(...durations);
          const requiredSlots = minDuration / BOOKING_CONSTANTS.WIDTH_PER_SLOT;

          let hour = Number(hourStr);
          let minute = Number(minuteStr);
          let allSlotsFree = true;

          for (let i = 0; i < requiredSlots; i++) {
            const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

            if (
              !slotData.availableSlots.includes(timeStr) ||
              slotData.occupiedSlots.includes(timeStr)
            ) {
              allSlotsFree = false;
              break;
            }

            minute += BOOKING_CONSTANTS.WIDTH_PER_SLOT;
            if (minute >= BOOKING_CONSTANTS.MINS_IN_HOUR) {
              hour += Math.floor(minute / BOOKING_CONSTANTS.MINS_IN_HOUR);
              minute %= BOOKING_CONSTANTS.MINS_IN_HOUR;
            }
          }

          if (allSlotsFree) {
            allowCount++;
          }
        }

        const slot: timeMapObj = { disabled: allowCount === 0, time };

        if (!groupedByHour[hourStr]) groupedByHour[hourStr] = [];
        groupedByHour[hourStr].push(slot);
      }
    });

    return Object.keys(groupedByHour)
      .sort((a, b) => Number(a) - Number(b))
      .map(hour => groupedByHour[hour]);
  };

  const getNextTimeMark = (
    targetTime: moment.Moment,
    interval: number
  ): moment.Moment => {
    const currentTime = moment();

    if (targetTime.isBefore(currentTime, DateFormats.Minutes)) {
      targetTime.hour(currentTime.hour()).minute(currentTime.minute());
    }

    const remainder = targetTime.minute() % interval;
    if (remainder !== 0) {
      targetTime.add(interval - remainder, DateFormats.Minutes);
    }

    if (interval === BOOKING_CONSTANTS.INTERVAL_30_MINUTES) {
      targetTime.minute(
        targetTime.minute() >= BOOKING_CONSTANTS.ROUNDED_30_MINUTE_MARK
          ? BOOKING_CONSTANTS.ROUNDED_30_MINUTE_MARK
          : BOOKING_CONSTANTS.ROUNDED_0_MINUTE_MARK
      );
    } else if (
      interval === BOOKING_CONSTANTS.INTERVAL_15_MINUTES &&
      targetTime.minute() >= BOOKING_CONSTANTS.MINS_IN_HOUR
    ) {
      targetTime.minute(BOOKING_CONSTANTS.ROUNDED_0_MINUTE_MARK);
    }

    return targetTime;
  };

  const generateTimeIntervals = (
    startTime: moment.Moment,
    endTime: moment.Moment,
    interval: number
  ): string[] => {
    const times: string[] = [];
    const currentTime = moment(startTime);

    while (currentTime.isSameOrBefore(endTime)) {
      times.push(currentTime.format(DateFormats.Time));
      currentTime.add(interval, DateFormats.Minutes);
    }

    if (
      times.length > 0 &&
      moment(times[times.length - 1], DateFormats.Time).isAfter(endTime)
    ) {
      times.pop();
    }

    return times;
  };

  const cancelBooking = () => {
    dispatch(bookingSlice.actions.flush(null));
  };

  const disableResource = (
    isBookingDisabled: boolean,
    slots: Array<AvailableTiming>
  ) => {
    if (slots?.length === 0 || isBookingDisabled) {
      return conditionalStyle.Disabled;
    } else {
      return conditionalStyle.None;
    }
  };

  const disableTimeMap = (selectedSlot: SingleSlot) => {
    return selectedSlot?.slot?.type === slotType?.ReservationSlots
      ? "disabled"
      : "";
  };

  const fetchSlots = async ({ bookingDate, locationId }: BookingSlotApi) => {
    const data = await API_SERVICES.BOOKING.GET_BOOKING_SLOT({
      bookingDate,
      locationId,
    })
      .then(response => {
        const responseData = handleResponse(response);
        if (responseData?.ok) {
          return response.data;
        }
      })
      .catch(() => {});
    return data;
  };

  const getSelectedResource = (
    slots: BookingFetchResponseType,
    resourceId = ""
  ) => {
    if (resourceId == "") resourceId = selectedSlot?.resourceId ?? "";

    const selectedResource = slots?.resources?.filter(
      obj => obj.id === resourceId
    )[0];
    return selectedResource;
  };

  const resourceActiveClass = (
    slot: AvailableTiming,
    resourceId: string
  ): string => {
    return selectedSlot?.resourceId === resourceId &&
      selectedSlot.slot?.slot === slot.slot &&
      selectedSlot?.isBookable
      ? "active"
      : "";
  };

  const selectSlot = (slotDetail: AvailableTiming, resourceId: string) => {
    const isSameSlot =
      selectedSlot?.resourceId === resourceId &&
      selectedSlot?.slot?.slot === slotDetail.slot;

    if (isSameSlot) {
      dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));
    } else {
      dispatch(
        bookingSlice.actions.changeSelectedSlot({
          isBookable: true,
          resourceId,
          slot: slotDetail,
        })
      );
    }

    scrollFocusToPayment();
  };

  const getAvailableDurations = (
    resourceDetails: ResourceType
  ): { durations: AvailableTiming[]; warningMessage: QuotaMessage } => {
    const finalSlots: AvailableTiming[] = [];

    const warningMessage: QuotaMessage = {
      advanceBookingMsg: "",
      quote: [],
    };

    if (resourceDetails?.quotaInfo) {
      QUOTA_INFO_ARRAY.forEach(quotaInfo => {
        if (resourceDetails?.quotaInfo?.[quotaInfo]) {
          warningMessage.quote.push(
            resourceDetails?.quotaInfo?.[quotaInfo]?.quotaMessage
          );
        }
      });
    }

    const availableSlots = resourceDetails.slotInfo.slots.availableSlots;
    const allowedDurations = resourceDetails.slotInfo.slots.allowedDurations;
    const occupiedSlots = resourceDetails.slotInfo.slots.occupiedSlots;
    const allowBringingGuest = resourceDetails.policyInfo.allowBringGuest;
    const key = `T_${selectedTime}`;

    if (allowedDurations[key]) {
      Object.values(allowedDurations[key]).forEach(durationObj => {
        const splitValue =
          durationObj.duration / BOOKING_CONSTANTS.WIDTH_PER_SLOT - 1;
        let slotAvailable = true;

        let timeMoment = moment(selectedTime, DateFormats.Time);
        const baseDate = moment(timeMoment.format(DateFormats.YMD));

        for (let i = 0; i <= splitValue; i++) {
          if (
            availableSlots.includes(timeMoment.format(DateFormats.Time)) &&
            !occupiedSlots.includes(timeMoment.format(DateFormats.Time)) &&
            moment(baseDate).isSame(moment(timeMoment.format(DateFormats.YMD)))
          ) {
            slotAvailable = true;
          } else {
            slotAvailable = false;
            break;
          }
          timeMoment = timeMoment.add(
            BOOKING_CONSTANTS.WIDTH_PER_SLOT,
            DateFormats.Minutes
          );
        }

        let isAdvancedBookable = true;

        const bookingEndAt: moment.Moment = moment(
          resourceDetails?.policyInfo?.bookingEndAt,
          DateFormats.YMDTimeWithSeconds
        );

        const selectedDateTimeMoment = moment(
          selectedDate + "T" + selectedTime,
          DateFormats.YMDTime
        );

        if (
          bookingEndAt.isValid() &&
          selectedDateTimeMoment.isAfter(bookingEndAt) &&
          !isEmpty(resourceDetails?.policyInfo?.bookingAdvanceType) &&
          resourceDetails?.policyInfo?.bookingAdvanceValue
        ) {
          isAdvancedBookable = false;

          const message = t("Calendar.advanceBookingMsg", {
            time: subtractBookingAdvanceTime(
              selectedDateTimeMoment,
              resourceDetails?.policyInfo?.bookingAdvanceValue,
              resourceDetails?.policyInfo?.bookingAdvanceType
            ),
          });

          warningMessage.advanceBookingMsg = message;
        }

        if (slotAvailable) {
          const availableObj: AvailableTiming = {
            allowBringingGuest: allowBringingGuest,
            availableForBooking: slotAvailable,
            disable: !isAdvancedBookable || warningMessage.quote.length > 0,
            ends: timeMoment.format(DateFormats.Time),
            max: durationObj.max ?? null,
            min: durationObj.min ?? null,
            slot: durationObj.duration,
            starts: selectedTime,
            type: slotType.TimeSlots,
          };
          finalSlots.push(availableObj);
        }
      });
    }

    return { durations: sortBy(finalSlots, "slot"), warningMessage };
  };

  const subtractBookingAdvanceTime = (
    momentDate: moment.Moment,
    amount: number,
    bookingAdvanceType: (typeof BOOKING_ADVANCE_DURATION)[keyof typeof BOOKING_ADVANCE_DURATION]
  ) => {
    switch (bookingAdvanceType) {
      case BOOKING_ADVANCE_DURATION.DAYS:
        momentDate = momentDate.subtract(amount, DateFormats.Days);
        break;
      case BOOKING_ADVANCE_DURATION.HOURS:
        momentDate = momentDate.subtract(amount, DateFormats.Hours);
        break;
      case BOOKING_ADVANCE_DURATION.MINUTES:
        momentDate = momentDate.subtract(amount, DateFormats.Minutes);
        break;
    }

    return momentDate.format(`${DateFormats.YMD} ${DateFormats.LT}`);
  };

  const gotoToday = () => {
    dispatch(
      bookingSlice.actions.changeCalenderStartDate(
        moment().format(DateFormats.YMD)
      )
    );
    dispatch(
      bookingSlice.actions.changeSelectedDate(moment().format(DateFormats.YMD))
    );
  };

  const selectDate = (date: string) => {
    dispatch(
      bookingSlice.actions.changeSelectedDate(
        moment(date).format(DateFormats.YMD)
      )
    );
  };

  const selectFuture = (date: string) => {
    if (
      moment().format(DateFormats.YMD) === moment(date).format(DateFormats.YMD)
    ) {
      dispatch(
        bookingSlice.actions.changeCalenderStartDate(
          moment(date).format(DateFormats.YMD)
        )
      );
      selectDate(date);
    } else if (
      moment().diff(date, DateFormats.Days) >= DateFormats.Minus5Days
    ) {
      dispatch(
        bookingSlice.actions.changeCalenderStartDate(
          moment(date)
            .subtract(
              Math.abs(moment().diff(date, DateFormats.Days)) + 1,
              DateFormats.Days
            )
            .format(DateFormats.YMD)
        )
      );
      selectDate(date);
    } else {
      dispatch(
        bookingSlice.actions.changeCalenderStartDate(
          moment(date)
            .subtract(DateFormats.Add5Days, DateFormats.Days)
            .format(DateFormats.YMD)
        )
      );
      selectDate(date);
    }
  };

  return {
    bookSlot,
    buildTimeSlot,
    cancelBooking,
    disableResource,
    disableTimeMap,
    fetchSlots,
    getAvailableDurations,
    getNextTimeMark,
    getSelectedResource,
    gotoToday,
    resourceActiveClass,
    scrollFocusToPayment,
    selectFuture,
    selectSlot,
    subtractBookingAdvanceTime,
  };
};
